"use client";

import Image from "next/image";

import Button from "@/components/button/Button";
import Heading from "@/components/heading/heading";

import Arrow from "@/public/svg/itinerary/arrow-down.svg";
import LocationIcon from "@/public/svg/itinerary/location-with-svg.svg";
import Tick from "@/public/svg/itinerary/tick-circle.svg";

import DimmakPicture from "@/public/png/itinerary/why-dimmak-mobile.png";

import { cn } from "@/utils/twMerge";
import { useState } from "react";
import "./itinerary-block.scss";

const DimmakList = [
    {
        info: "Premium All Inclusive Tour",
    },
    {
        info: "24X7 On-Trip Assistance",
    },
    {
        info: "Destination Experts",
    },
];

const itineraryData = [
    {
        text: "Arrive in Colombo and travel to Kandy, stopping at the Pinnawala Elephant Orphanage to observe rescued elephants in a semi-natural setting, and a Spice Garden to learn about local herbs and spices. Visit the Temple of the Tooth, a significant Buddhist site, before checking in at Royal Kandyan for an overnight stay in Kandy.",
        activities: 4,
        sightseeing: 0,
        listItems: [
            "Pinnawala Elephant Orphanage",
            "Spice Garden",
            "Temple of the Tooth",
            "Check-in at Royal Kandyan",
        ],
        date: "Day 1 (April 30)",
        heading: "Kandy Adventure",
    },
    {
        text: "Drive to Nuwara Eliya and explore the Gem Museum, followed by a visit to the picturesque Ramboda Falls. Learn about Sri Lanka’s tea culture at a Tea Factory, then unwind by Gregory Lake. Continue to Hakgala Botanical Garden to admire its lush flora before checking in at Ashford Hotel for an overnight stay in Nuwara Eliya.",
        activities: 6,
        sightseeing: 0,
        listItems: [
            "Gem Museum",
            "Ramboda Falls",
            "Tea Factory",
            "Gregory Lake",
            "Hakgala Botanical Garden",
            "Check-in at Ashford Hotel",
        ],
        date: " Day 2 (May 01)",
        heading: "Nuwara Eliya Adventure",
    },
    {
        text: "Drive to Bentota, stopping at a Turtle Hatchery for a glimpse of sea turtle conservation. Enjoy a scenic Madu River boat ride, with optional water sports along the way. Check in at Hibiscus Beach Resort for an overnight stay in Bentota.",
        activities: 3,
        sightseeing: 0,
        listItems: [
            "Turtle Hatchery",
            "Madu River Boat Ride",
            "Overnight at Hibiscus Beach Resort",
        ],
        date: "Day 3 (May 02)",
        heading: "Bentota Adventure",
    },
    {
        text: "Drive to Colombo, visiting Gangaramaya Temple, Colombo Museum, and Lotus Tower to explore cultural heritage and modern cityscapes. Check in at Best Western Colombo for an overnight stay in the city.",
        activities: 4,
        sightseeing: 0,
        listItems: [
            "Gangaramaya Temple",
            "Colombo Museum",
            "Lotus Tower",
            "Overnight at Best Western Colombo",
          
        ],
        date: "Day 4 (May 03)",
        heading: "Colombo Adventure",
    },
    {
        text: "Enjoy free time for last-minute shopping before your airport drop-off. Depart on your return flight, bringing your Sri Lankan journey to a close.",
        activities: 0,
        sightseeing: 0,
        listItems: ["Free time for shopping","Airport drop-off","Return flight"],
        date: "Day 5 (May 04) ",
        heading: "Final Day",
    },
];

const Card = ({
    opened,
    onChange,
    data,
}: {
    opened: boolean;
    onChange: () => void;
    data: {
        text: string;
        activities: number;
        sightseeing: number;
        listItems: string[];
        date: string;
        heading: string;
    };
}) => {
    const { text, activities, sightseeing, listItems, date, heading } = data;
    return (
        <div
            className={cn(
                "collapse-expand flex flex-col",
                opened ? "open" : "close"
            )}
        >
            <div className="collapse-expand-header flex items-center justify-between">
                <div
                    className="flex items-center gap-4 cursor-pointer"
                    onClick={onChange}
                >
                    <Image
                        loading="lazy"
                        src={LocationIcon}
                        alt="Location Icon"
                        width={40}
                        height={40}
                    />
                    <Heading as="h6">{heading}</Heading>
                </div>
                <div
                    className="arrow flex items-center gap-3 py-2 px-3"
                    onClick={onChange}
                >
                    <span className="text-sm font-medium">{date}</span>
                    <Image
                        loading="lazy"
                        src={Arrow}
                        alt="logo"
                        width={18}
                        height={18}
                    />
                </div>
            </div>
            <div className="collapse-expand-body flex flex-col pl-14 gap-4 items-start">
                <p>{text}</p>
                {activities > 0 ? (
                    <Button className="flex-center" variant="green">
                        <span>
                            {activities}{" "}
                            {activities > 1 ? "Activities" : "Activity"}
                        </span>
                    </Button>
                ) : (
                    ""
                )}
                {sightseeing > 0 ? (
                    <Button className="flex-center" variant="green">
                        <span>
                            {sightseeing}{" "}
                            {sightseeing > 1 ? "Sightseeings" : "Sightseeing"}
                        </span>
                    </Button>
                ) : (
                    ""
                )}
                <ul className="list">
                    {listItems.map((item, index) => {
                        return (
                            <li className="list-item" key={index}>
                                <span className="lead">{item}</span>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

const ItineraryBlock = () => {
    const [opened, setOpened] = useState<Array<number>>([0]);

    const handleChange = (index: number) => {
        if (opened.includes(index)) {
            setOpened((prev) => prev.filter((item) => item !== index));
        } else {
            setOpened((prev) => [...prev, index]);
        }
    };

    return (
        <div className="itinerary-block py-9 flex flex-col gap-6 relative">
            <span id="itinerary" className="anchor"></span>
            <Heading as="h2">Day To Day Itinerary</Heading>
            <div className="collapse-expand-wrapper flex flex-col gap-10">
                {itineraryData.map((item, index) => {
                    return (
                        <Card
                            key={index}
                            onChange={() => handleChange(index)}
                            opened={opened.includes(index)}
                            data={itineraryData[index]}
                        />
                    );
                })}
            </div>
            <div className="dimmak-block flex flex-col xl:py-8 xl:px-6">
                <div className="dimmak-content flex flex-col pt-6 px-4 sm:pt-8 sm:px-6 xl:pt-0 xl:px-0  gap-6">
                    <Heading as="h3">Why travel with Dimaak?</Heading>
                    <ul className="dimmak-list flex flex-wrap items-center gap-3">
                        {DimmakList.map((d, index) => {
                            return (
                                <li
                                    className="dimmak-list-item flex items-center p-3 gap-2"
                                    key={index}
                                >
                                    <figure className="shrink-0">
                                        <Image
                                            loading="lazy"
                                            src={Tick}
                                            alt="Tick"
                                            width={44}
                                            height={44}
                                        />
                                    </figure>
                                    <span className="font-light">{d.info}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <figure className="dimmak-picture-mobile">
                    <Image
                        loading="lazy"
                        src={DimmakPicture}
                        alt="Dimaak Picture"
                    />
                </figure>
            </div>
        </div>
    );
};

export default ItineraryBlock;
